/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import FeaturesService from 'ember-feature-flags/services/features';
import TransactionalMessageModel, {
  TransactionalMessageChannel as Channel,
  TransactionalMessageChannels,
  TransactionalMessageChannelType,
} from 'cio/models/transactional-message';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import DeliveryService from 'cio/services/delivery';
import ApiErrorsService from 'cio/services/api-errors';

interface ChannelListArgs {
  message: TransactionalMessageModel;
  onSelect: (channelType: TransactionalMessageChannelType) => unknown;
}

export default class ChannelList extends Component<ChannelListArgs> {
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;
  @service('delivery') deliveryService!: DeliveryService;
  @service apiErrors!: ApiErrorsService;

  @tracked pendingChannel?: Channel;
  @tracked disabledChannelType?: TransactionalMessageChannelType;

  constructor(owner: unknown, args: ChannelListArgs) {
    super(owner, args);
    this.fetchDeliveriesTask.perform();
  }

  @computed('disabledChannelType')
  get channels() {
    return TransactionalMessageChannels.map((channel: Channel) => this.addChannelStateInfo(channel));
  }

  private addChannelStateInfo(channel: Channel) {
    let info = {
      enabled: this.environment.enabledAction(channel.type) && this.disabledChannelType !== channel.type,
      tooltipContent: '',
    };

    if (this.disabledChannelType === channel.type) {
      info.tooltipContent = "The channel can't be changed once a message has been sent.";
    }

    return {
      ...channel,
      info: {
        ...channel.info,
        ...info,
      },
    };
  }

  @action
  onCancelChannelChange() {
    this.pendingChannel = undefined;
  }

  @action
  onConfirmChannelChange(channel: Channel) {
    this.args.onSelect(channel.type);
    this.pendingChannel = undefined;
  }

  @action
  onChannelChange(channel: Channel) {
    let { message } = this.args;

    if (message.type === channel.type) {
      return;
    }

    if (!message.hasTemplateContent) {
      this.onConfirmChannelChange(channel);
      return;
    }

    this.pendingChannel = channel;
  }

  fetchDeliveriesTask = task({ drop: true }, async () => {
    try {
      let { type, id } = this.args.message;

      let deliveries = await this.deliveryService.fetchTransactionalDeliveries(id, type);

      if (deliveries.length > 0) {
        this.disabledChannelType =
          type === TransactionalMessageChannelType.Email
            ? TransactionalMessageChannelType.Push
            : TransactionalMessageChannelType.Email;
      }
    } catch (e) {
      this.apiErrors.report(e);
    }
  });
}
